import { FC, useCallback, useEffect, useState, type Key, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { CloseOutlined, PrinterOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { PDFDocument, type PDFPage } from "pdf-lib";
import PdfViewerModal from "components/PrintShippingLabel/PdfViewerModal";
import type { ColumnsType } from "antd/es/table";
import { useGetTransportBrands } from "hooks/useOrders";
import type { PartnerTransportBrand } from "services/interface";
import { formatWithCommas } from "components/ManageProduct/CreateProduct/ProductSKUCreate/helpers";
import { carrierCodesMatch, normalizeSpeedLogo } from "../ParcelShared/speedCarrierUtils";
import {
  cancelFactoryParcel,
  FactoryParcelAddress,
  FactoryParcelItem,
  FactoryParcelPackagingSnapshot,
  FactoryParcelProduct,
  listFactoryParcels,
  markFactoryParcelPrint,
  printFactoryParcel,
  retryFactoryParcel,
} from "services/factoryParcelApi";
import { useParcelLabelPreview } from "../ParcelShared/useParcelLabelPreview";
import { extractParcelLabelUrl } from "../ParcelShared/parcelLabelPrint";
import ParcelSearchForm from "./ParcelSearchForm";
import { MANAGE_PARCEL_CREATE_PATH } from "../ParcelShared/parcelRoutes";
import { PARCEL_LIST_REFRESH_EVENT } from "../ParcelShared/parcelListEvents";
import ParcelCreditCard from "../ParcelShared/ParcelCreditCard";
import InsufficientCreditModal from "../ParcelShared/InsufficientCreditModal";
import { useFactoryCreditBalance, useFactoryCodCreditInfo } from "hooks/useFactoryCredit";
import {
  PARCEL_PENDING_QUEUE_LINE1,
  PARCEL_PENDING_QUEUE_LINE2,
  PARCEL_PENDING_QUEUE_TAG_COLOR,
  parcelListNeedsQueuePoll,
  resolveParcelTableStatusDisplay,
} from "../ParcelShared/parcelSpeedQueueLabels";
import {
  ParcelCardWrapperTable,
  ParcelHeadWrapper,
  ParcelRightIconWrapper,
  ParcelWrapperAll,
  PARCEL_TABLE_FONT,
  PARCEL_TABLE_FONT_SECONDARY,
  PARCEL_TABLE_SECONDARY_COLOR,
  parcelTableIconActionButtonStyle,
} from "../manageParcelLayout";

/**
 * รองรับ 2 format จาก API:
 *   "2026-06-10 08:04:50.031"   — pg raw string (TIMESTAMP, no tz) → parse ตรงเป็น local
 *   "2026-06-10T01:04:50.031Z" — ISO UTC (TypeORM serialized Date)  → ให้ Date จัดการ
 */
function parseParcelDateParts(date: string): { year: number; month: number; day: number; hour: number; minute: number } {
  if (date.includes("T")) {
    const d = new Date(date);
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), hour: d.getHours(), minute: d.getMinutes() };
  }
  const [datePart = "", timePart = ""] = date.split(" ");
  const [year = 0, month = 0, day = 0] = datePart.split("-").map(Number);
  const [hour = 0, minute = 0] = timePart.split(":").map(Number);
  return { year, month, day, hour, minute };
}
function formatParcelDateTime(date: string): string {
  const { day, month, year, hour, minute } = parseParcelDateParts(date);
  return `${day}/${month}/${year} ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}



const STATUS_COLOR: Record<string, string> = {
  pending_ship: "purple",
  waiting: "orange",
  on_delivery: "blue",
  delivered: "green",
  canceled: "default",
  on_return: "volcano",
  returned: "purple",
  claimed: "gold",
};

/** แปลง status code จาก API เป็นข้อความภาษาไทย */
const PARCEL_STATUS_LABEL_TH: Record<string, string> = {
  waiting: "รอขนส่งเข้ารับ",
  on_delivery: "กำลังจัดส่ง",
  delivered: "จัดส่งสำเร็จ",
  canceled: "ยกเลิกแล้ว",
  on_return: "กำลังส่งคืน",
  returned: "ส่งคืนแล้ว",
  claimed: "เคลมแล้ว",
};

const getBrandValue = (b: PartnerTransportBrand) =>
  b.brand_code != null && b.brand_code !== "" ? b.brand_code : b.id;

const MAX_PARCEL_MERGE_COUNT = 100;

const isValidParcelLabelUrl = (url?: string | null): boolean => {
  if (!url?.trim()) return false;
  const t = url.trim();
  return t.startsWith("http://") || t.startsWith("https://");
};

/** ส่งออกจากโรงงานแล้ว (ไม่ใช่รอขนส่งเข้ารับ) */
const isParcelAlreadyShipped = (record: FactoryParcelItem): boolean =>
  !!record.status && record.status !== "waiting";

/** ยกเลิกได้ถึงแค่ 15:00 ของวันถัดจากเวลาที่ได้ label (fallback เป็น create_date ถ้ายังไม่มี) — ตรงกับ policy ของ Speed */
const isParcelCancelable = (record: FactoryParcelItem): boolean => {
  const source = record.label_obtained_at || record.create_date;
  if (!source) return false;
  const { year, month, day, hour, minute } = parseParcelDateParts(String(source));
  const created = new Date(year, month - 1, day, hour, minute);
  if (isNaN(created.getTime())) return false;
  const deadline = new Date(created);
  deadline.setDate(deadline.getDate() + 1);
  deadline.setHours(15, 0, 0, 0);
  return Date.now() < deadline.getTime();
};

/** แถวที่เลือก checkbox / พิมพ์รวมได้ — ไม่รวมที่ส่งแล้วและพิมพ์แล้ว */
const isParcelRowSelectable = (record: FactoryParcelItem): boolean =>
  !!record.tracking_number?.trim() &&
  record.status !== "canceled" &&
  !isParcelAlreadyShipped(record) &&
  !Boolean(record.is_print);

const getParcelTrackingNumber = (record: FactoryParcelItem): string | null => {
  const t = record.tracking_number?.trim();
  return t || null;
};

const VIEWPOINT_OPTIONS = [

  { value: "all", label: "ทั้งหมด" },

  { value: "waiting", label: "รอขนส่งเข้ารับ" },

  { value: "on_delivery", label: "กำลังจัดส่ง" },

  { value: "delivered", label: "จัดส่งสำเร็จ" },

  { value: "canceled", label: "ยกเลิก" },

];

/** ห่อ cell ให้แสดง tooltip เต็มเมื่อ hover (ใช้กับ Tag / ปุ่ม) */
const wrapParcelCellTooltip = (title: string, node: ReactNode) => (
  <Tooltip title={title}>
    <span style={{ display: "inline-block", maxWidth: "100%", verticalAlign: "top" }}>
      {node}
    </span>
  </Tooltip>
);

const formatDestinationAdminLine = (dest: FactoryParcelAddress): string =>
  [
    dest.county?.trim() ? `ตำบล ${dest.county.trim()}` : null,
    dest.city?.trim() ? `อำเภอ ${dest.city.trim()}` : null,
    dest.state?.trim() ? `จังหวัด ${dest.state.trim()}` : null,
    dest.postcode?.trim() ? dest.postcode.trim() : null,
  ]
    .filter(Boolean)
    .join(" ");

const renderDestinationAddressCell = (dest?: FactoryParcelAddress) => {
  if (!dest) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: PARCEL_TABLE_FONT }}>
        -
      </Typography.Text>
    );
  }
  const name = dest.fullname?.trim() || "-";
  const adminLine = formatDestinationAdminLine(dest);
  const addressLine = [dest.address?.trim(), adminLine].filter(Boolean).join(" ");
  const fullText = addressLine ? `${name}\n${addressLine}` : name;

  return (
    <Typography.Text ellipsis={{ tooltip: fullText }} style={{ fontSize: PARCEL_TABLE_FONT }}>
      <span style={{ display: "block", fontWeight: 500 }}>{name}</span>
      {addressLine ? (
        <span
          style={{
            display: "block",
            fontSize: PARCEL_TABLE_FONT_SECONDARY,
            color: PARCEL_TABLE_SECONDARY_COLOR,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {addressLine}
        </span>
      ) : null}
    </Typography.Text>
  );
};

const renderDestinationPhoneCell = (dest?: FactoryParcelAddress) => {
  const text = dest?.telephone1?.trim() || "-";
  return (
    <Typography.Text ellipsis={{ tooltip: text }} style={{ fontSize: PARCEL_TABLE_FONT }}>
      {text}
    </Typography.Text>
  );
};

const formatProductsLine1 = (products?: FactoryParcelProduct[]): string => {
  if (!products?.length) return "-";
  return products.map((p) => p.name?.trim() || p.code?.trim() || "-").join(", ");
};

const formatPackagingLine2 = (snap?: FactoryParcelPackagingSnapshot | null): string => {
  if (!snap) return "";
  const parts: string[] = [];
  if (snap.type_label?.trim()) {
    parts.push(`บรรจุภัณฑ์ ${snap.type_label.trim()}`);
  }
  const w = snap.box_width;
  const l = snap.box_length;
  const h = snap.box_height;
  if (w != null || l != null || h != null) {
    parts.push(
      `กว้าง ${w ?? "-"} ยาว ${l ?? "-"} สูง ${h ?? "-"} ซ.ม.`
    );
  }
  if (snap.box_weight != null) {
    parts.push(`น้ำหนัก ${snap.box_weight} ก.`);
  }
  return parts.join(" · ");
};

const renderProductsCell = (record: FactoryParcelItem) => {
  const line1 = formatProductsLine1(record.products_snapshot);
  const line2 = formatPackagingLine2(record.packaging_snapshot);
  const fullText = line2 ? `${line1}\n${line2}` : line1;

  return (
    <Typography.Text ellipsis={{ tooltip: fullText }} style={{ fontSize: PARCEL_TABLE_FONT }}>
      <span style={{ display: "block", fontWeight: 500 }}>{line1}</span>
      {line2 ? (
        <span
          style={{
            display: "block",
            fontSize: PARCEL_TABLE_FONT_SECONDARY,
            color: PARCEL_TABLE_SECONDARY_COLOR,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {line2}
        </span>
      ) : null}
    </Typography.Text>
  );
};

/**
 * "เครดิตที่ใช้" ต่อพัสดุ — เดิมคอลัมน์นี้ชื่อ "ราคา (บาท)" โชว์ยอดดิบตรงๆ ไม่ตรงกับโควต้าที่
 * โรงงานเห็นจริงบนการ์ดเครดิต (ParcelCreditCard ใช้ available_credit × multiplier เหมือนกัน)
 * เปลี่ยนชื่อคอลัมน์ + คูณ multiplier ให้ตรงกัน (2026-08-24)
 */
const formatParcelPrice = (record: FactoryParcelItem, multiplier: number): string => {
  const withdraw = record.withdraw;
  const charge = record.charge;
  const estimate = record.estimate_price;
  const raw =
    withdraw != null && Number(withdraw) > 0
      ? Number(withdraw)
      : charge != null && Number(charge) > 0
        ? Number(charge)
        : withdraw != null
          ? Number(withdraw)
          : charge != null
            ? Number(charge)
            : estimate != null
              ? Number(estimate)
              : null;
  if (raw == null || Number.isNaN(raw)) return "-";
  return formatWithCommas(Math.ceil(raw) * multiplier);
};

const renderBrandCell = (
  record: FactoryParcelItem,
  brandList: PartnerTransportBrand[]
) => {
  const brand = brandList.find((b) =>
    carrierCodesMatch(getBrandValue(b), record.carrier_code)
  );
  const logo = normalizeSpeedLogo(brand?.brand_logo);
  const name = brand?.brand_name ?? record.carrier_code ?? "-";
  if (!logo && !brand?.brand_name) {
    return (
      <Typography.Text
        type="secondary"
        ellipsis={{ tooltip: name }}
        style={{ fontSize: PARCEL_TABLE_FONT }}
      >
        {name}
      </Typography.Text>
    );
  }
  return (
    <Space size={4} align="start" style={{ width: "100%", minWidth: 0 }}>
      {logo ? (
        <Avatar
          src={logo}
          alt={name}
          shape="square"
          size={20}
          style={{ flexShrink: 0 }}
        />
      ) : null}
      <Typography.Text
        ellipsis={{ tooltip: name }}
        style={{ fontSize: PARCEL_TABLE_FONT_SECONDARY, minWidth: 0 }}
      >
        {name}
      </Typography.Text>
    </Space>
  );
};

const ParcelListTab: FC = () => {
  const navigate = useNavigate();
  const { openPreview: openLabelPreview, previewModal } =
    useParcelLabelPreview({ onMarkedPrint: () => void load() });
  const [items, setItems] = useState<FactoryParcelItem[]>([]);

  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);

  const [limit, setLimit] = useState(20);

  const [loading, setLoading] = useState(false);

  const [viewpoint, setViewpoint] = useState("all");

  const [searchInput, setSearchInput] = useState("");

  const [dateInput, setDateInput] = useState("");

  const [selectedBrandCodes, setSelectedBrandCodes] = useState<string[]>([]);

  const { data: brandsData } = useGetTransportBrands();
  const brandList = brandsData?.data ?? [];

  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergePreviewOpen, setMergePreviewOpen] = useState(false);
  const [mergedBlobUrl, setMergedBlobUrl] = useState<string | null>(null);
  const [pendingPrintParcelIds, setPendingPrintParcelIds] = useState<string[]>([]);
  /** จำนวนแถวที่ผู้ใช้เลือกตอนกดพิมพ์รวม (ใช้ควบ UI หลายหน้า ไม่ใช่แค่จำนวนที่โหลด label สำเร็จ) */
  const [mergeSelectionCount, setMergeSelectionCount] = useState(0);
  const [retryingParcelId, setRetryingParcelId] = useState<string | null>(null);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [creditModalPrice, setCreditModalPrice] = useState<number | null>(null);
  const { data: creditData } = useFactoryCreditBalance();
  // ดึงจาก master_bangbow.minimum_parcel_credit จริง (เดิม hardcode 500 ไว้ในโค้ด ไม่ตรงกับค่าจริงใน DB)
  const minimumCredit = creditData?.minimum_parcel_credit ?? 20;
  const hasEnoughCredit = (creditData?.available_credit ?? 0) >= minimumCredit;
  // multiplier ปัจจุบัน — ใช้แค่แสดงคอลัมน์ "เครดิตที่ใช้" ให้ตรงกับโควต้าจริงที่โรงงานเห็น (ParcelCreditCard)
  const { data: codCreditInfo } = useFactoryCodCreditInfo();
  const multiplier = codCreditInfo?.cod_credit_multiplier ?? 2;
  // 2026-09-02: minimumCredit ที่ดึงจาก DB (master_bangbow.minimum_parcel_credit) เป็นค่า raw (500) —
  // ใช้เทียบ hasEnoughCredit ตรงๆ ได้เพราะ available_credit ก็เป็น raw เหมือนกัน (raw vs raw ถูกต้อง
  // แล้ว ห้ามคูณ multiplier ตรงจุดเช็คนี้) แต่ตัวเลขที่ "แสดงผล" ให้ผู้ใช้เห็นต้อง × multiplier เสมอ
  // เหมือนทุกที่อื่นในระบบ (การ์ด "เครดิตที่ใช้ได้" ก็โชว์ค่า × multiplier แล้ว) ไม่งั้นตัวเลขในข้อความ
  // เตือนจะไม่ตรงกับตัวเลขที่โรงงานเห็นบนการ์ดจริง (DB จริง 500 × multiplier 2 = 1,000)
  const minimumCreditDisplay = Math.round(minimumCredit * multiplier);

  const load = useCallback(async () => {

    setLoading(true);

    try {

      const res = await listFactoryParcels({

        viewpoint,

        search: searchInput || undefined,

        start: dateInput || undefined,

        end: dateInput || undefined,

        carrier_codes:
          selectedBrandCodes.length > 0
            ? selectedBrandCodes.join(",")
            : undefined,

        page,

        limit,

      });

      if (res.status === "success" && res.data) {

        setItems(res.data.items);

        setTotal(res.data.total);

      }

    } catch {

      message.error("โหลดรายการไม่สำเร็จ");

    } finally {

      setLoading(false);

    }

  }, [viewpoint, searchInput, dateInput, selectedBrandCodes, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onSocketRefresh = () => {
      void load();
    };
    window.addEventListener(PARCEL_LIST_REFRESH_EVENT, onSocketRefresh);
    return () => {
      window.removeEventListener(PARCEL_LIST_REFRESH_EVENT, onSocketRefresh);
    };
  }, [load]);

  useEffect(() => {
    if (!parcelListNeedsQueuePoll(items)) return;
    const timer = window.setInterval(() => {
      void load();
    }, 60000);
    return () => window.clearInterval(timer);
  }, [items, load]);

  useEffect(() => {
    setSelectedRowKeys((prev) =>
      prev.filter((k) => items.some((i) => i.id === k && isParcelRowSelectable(i)))
    );
  }, [items]);

  const getSelectableRowKeys = useCallback((): Key[] => {
    return items.filter(isParcelRowSelectable).map((r) => r.id);
  }, [items]);

  const resolveParcelLabelUrl = async (row: FactoryParcelItem): Promise<string | null> => {
    const tracking = getParcelTrackingNumber(row);
    if (!tracking) return null;
    if (isValidParcelLabelUrl(row.label_url)) {
      return row.label_url!.trim();
    }
    try {
      const res = await printFactoryParcel({
        tracking_number: tracking,
        carrier_code: row.carrier_code,
      });
      return extractParcelLabelUrl(res);
    } catch {
      return null;
    }
  };

  const closeMergePreview = () => {
    if (mergedBlobUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(mergedBlobUrl);
    }
    setMergePreviewOpen(false);
    setMergedBlobUrl(null);
    setPendingPrintParcelIds([]);
    setMergeSelectionCount(0);
  };

  const handleMergePreviewAfterPrint = () => {
    const ids = pendingPrintParcelIds;
    if (!ids.length) return;
    markFactoryParcelPrint({ parcel_ids: ids })
      .then((result) => {
        void load();
        setSelectedRowKeys([]);
        if (result?.data?.updated_count === 0) {
          message.warning("ไม่มีรายการที่อัปเดตสถานะพิมพ์ได้");
        } else if (typeof result?.data?.updated_count === "number") {
          message.success(`บันทึกสถานะพิมพ์แล้ว ${result.data.updated_count} รายการ`);
        } else {
          message.success("บันทึกสถานะพิมพ์แล้ว");
        }
      })
      .catch(() => {
        message.warning("ส่งพิมพ์แล้ว แต่บันทึกสถานะพิมพ์ไม่สำเร็จ");
      });
    closeMergePreview();
  };

  const handlePrintAll = async () => {
    const keys = selectedRowKeys as string[];
    if (!keys.length) return;
    const selectedRows = keys
      .map((id) => items.find((r) => r.id === id))
      .filter((r): r is FactoryParcelItem => !!r && isParcelRowSelectable(r));
    if (!selectedRows.length) {
      message.warning("ไม่มีรายการที่เลือกได้");
      return;
    }
    if (selectedRows.length > MAX_PARCEL_MERGE_COUNT) {
      message.warning(`รวมได้สูงสุด ${MAX_PARCEL_MERGE_COUNT} รายการ กรุณาลดการเลือก`);
      return;
    }
    setMergeLoading(true);
    try {
      const urlResults = await Promise.all(
        selectedRows.map(async (row) => ({
          id: row.id,
          url: await resolveParcelLabelUrl(row),
        }))
      );
      const withUrl = urlResults.filter((x) => x.url);
      if (!withUrl.length) {
        message.warning("ไม่มี Label ที่โหลดได้จากรายการที่เลือก");
        return;
      }
      if (withUrl.length < selectedRows.length) {
        message.warning(
          `โหลด Label ได้ ${withUrl.length} จาก ${selectedRows.length} รายการ — จะรวมเฉพาะที่โหลดได้`
        );
      }
      const buffers = await Promise.all(
        withUrl.map(async ({ url }) => {
          const res = await fetch(url!);
          if (!res.ok) throw new Error(`โหลด PDF ไม่สำเร็จ: ${url}`);
          return res.arrayBuffer();
        })
      );
      const mergedPdf = await PDFDocument.create();
      for (const bytes of buffers) {
        const pdf = await PDFDocument.load(bytes);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page: PDFPage) => mergedPdf.addPage(page));
      }
      const mergedBytes = await mergedPdf.save();
      const blob = new Blob([new Uint8Array(mergedBytes)], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      setMergedBlobUrl(blobUrl);
      setPendingPrintParcelIds(withUrl.map((x) => x.id));
      setMergeSelectionCount(selectedRows.length);
      setMergePreviewOpen(true);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "รวม PDF ไม่สำเร็จ (อาจติด CORS หรือลิงก์ไม่ถูกต้อง)";
      message.error(msg);
    } finally {
      setMergeLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: Key[]) => setSelectedRowKeys(keys),
    onSelectAll: (selected: boolean) => {
      if (selected) {
        setSelectedRowKeys(getSelectableRowKeys());
      } else {
        setSelectedRowKeys([]);
      }
    },
    getCheckboxProps: (record: FactoryParcelItem) => ({
      disabled: !isParcelRowSelectable(record),
      style: !isParcelRowSelectable(record) ? { display: "none" } : undefined,
    }),
    renderCell: (
      _checked: boolean,
      record: FactoryParcelItem,
      _index: number,
      originNode: React.ReactNode
    ) => (isParcelRowSelectable(record) ? originNode : null),
  };

  const handlePrint = (row: FactoryParcelItem) => {
    const tracking = getParcelTrackingNumber(row);
    if (!tracking) {
      message.warning("ยังไม่มีเลขพัสดุ");
      return;
    }
    void openLabelPreview({
      parcel_id: row.id,
      tracking_number: tracking,
      carrier_code: row.carrier_code,
      label_url: row.label_url ?? undefined,
      title: tracking,
    });
  };



  const handleCancel = async (tracking: string) => {

    try {

      await cancelFactoryParcel(tracking);

      message.success("ยกเลิกพัสดุแล้ว");

      load();

    } catch (err) {

      const backendMessage = (err as { message?: string })?.message;
      message.error(backendMessage || "ยกเลิกไม่สำเร็จ");
      void load();

    }

  };

  const handleRetryQueue = async (row: FactoryParcelItem) => {
    // FAILED parcels need a new credit deduction — check before calling API
    // หมายเหตุ 2026-08-25: เดิมเช็คกับ minimumCredit (master_bangbow.minimum_parcel_credit = 500) ซึ่ง
    // เป็น threshold "ควรเติมเครดิต" สำหรับ gate ปุ่มสร้างรายการใหม่ (flow คนละแบบกับ retry) ทำให้ retry
    // ถูกบล็อกทั้งที่เครดิตพอจ่ายค่าพัสดุจริง (เช่น available=460 ต้องการ 60 → เหลือ 400 ยังพอมาก แต่
    // 400 < 500 เลยโดนบล็อก) — retry ควรเทียบแค่ราคาพัสดุจริง เหมือน handleOpenConfirm ใน
    // ParcelCreateForm.tsx (ไม่ผูกกับ minimum_parcel_credit) ให้ตรงกับ backend เองที่ retryParcel()
    // อนุญาตด้วย buffer เล็กน้อยเท่านั้น ไม่ใช่ 500
    if (row.speed_queue_status === "FAILED") {
      const cost = Math.ceil(Number(row.estimate_price) || 0);
      if (cost > 0 && (creditData?.available_credit ?? 0) < cost) {
        setCreditModalPrice(cost);
        setCreditModalOpen(true);
        return;
      }
    }
    setRetryingParcelId(row.id);
    try {
      const res = await retryFactoryParcel(row.id);
      if (res.status === "success") {
        if (res.data?.tracking_number) {
          message.success(`ได้เลขพัสดุแล้ว: ${res.data.tracking_number}`);
        } else {
          message.success(
            "ส่งคำขอลองใหม่แล้ว — กำลังเชื่อมต่อกับระบบขนส่งเพื่อขอหมายเลขพัสดุ"
          );
        }
        void load();
      } else {
        // backend ส่ง HTTP 200 เสมอแม้ error (ดู http-exception.filter.ts) — ข้อความจริงอยู่ใน error_message
        const backendMsg = Array.isArray(res.error_message)
          ? res.error_message.join(", ")
          : res.error_message;
        message.error(backendMsg || "ลองใหม่ไม่สำเร็จ");
      }
    } catch {
      message.error("ลองใหม่ไม่สำเร็จ");
    } finally {
      setRetryingParcelId(null);
    }
  };



  const columns: ColumnsType<FactoryParcelItem> = [
    {
      title: "เลขที่อ้างอิง",
      dataIndex: "reference_no",
      key: "reference_no",
      width: 100,
      ellipsis: true,
      render: (v: string) => {
        const text = v?.trim() || "-";
        return (
          <Typography.Text strong ellipsis={{ tooltip: text }} style={{ fontSize: PARCEL_TABLE_FONT }}>
            {text}
          </Typography.Text>
        );
      },
    },
    {
      title: "ชื่อและที่อยู่ผู้รับ",
      key: "destination_address",
      width: 140,
      ellipsis: true,
      render: (_: unknown, record) =>
        renderDestinationAddressCell(record.destination_snapshot),
    },
    {
      title: "เบอร์โทร",
      key: "destination_phone",
      width: 90,
      ellipsis: true,
      render: (_: unknown, record) =>
        renderDestinationPhoneCell(record.destination_snapshot),
    },
    {
      title: "แบรนด์",
      key: "brand",
      width: 56,
      ellipsis: true,
      render: (_: unknown, record) => renderBrandCell(record, brandList),
    },
    {
      title: "เลขพัสดุ",
      dataIndex: "tracking_number",
      key: "tracking_number",
      width: 88,
      ellipsis: true,
      render: (t: string) => {
        const text = t || "-";
        return (
          <Typography.Text copyable={!!t} ellipsis={{ tooltip: text }} style={{ fontSize: PARCEL_TABLE_FONT }}>
            {text}
          </Typography.Text>
        );
      },
    },
    {
      title: "รายการพัสดุ",
      key: "products",
      width: 150,
      ellipsis: true,
      render: (_: unknown, record) => renderProductsCell(record),
    },
    {
      title: "เครดิตที่ใช้",
      key: "price",
      width: 64,
      align: "right",
      render: (_: unknown, record) => {
        const text = formatParcelPrice(record, multiplier);
        return (
          <Typography.Text ellipsis={{ tooltip: text }} style={{ fontSize: PARCEL_TABLE_FONT }}>
            {text}
          </Typography.Text>
        );
      },
    },
    {
      title: "วันที่สร้าง",
      dataIndex: "create_date",
      key: "create_date",
      width: 78,
      ellipsis: true,
      render: (d) => {
        const str = d ? formatParcelDateTime(String(d)) : "-";
        return (
          <Typography.Text ellipsis={{ tooltip: str }} style={{ fontSize: PARCEL_TABLE_FONT }}>
            {str}
          </Typography.Text>
        );
      },
    },
    {
      title: "สถานะการพิมพ์",
      dataIndex: "is_print",
      key: "is_print",
      width: 64,
      render: (printed: boolean | undefined, record: FactoryParcelItem) => {
        if (!record.tracking_number) {
          return wrapParcelCellTooltip(
            "-",
            <Typography.Text type="secondary" style={{ fontSize: PARCEL_TABLE_FONT }}>
              -
            </Typography.Text>,
          );
        }
        const label = printed ? "พิมพ์แล้ว" : "ยังไม่พิมพ์";
        return wrapParcelCellTooltip(
          label,
          printed ? (
            <Tag color="green" style={{ margin: 0, fontSize: PARCEL_TABLE_FONT_SECONDARY }}>
              พิมพ์แล้ว
            </Tag>
          ) : (
            <Tag color="default" style={{ margin: 0, fontSize: PARCEL_TABLE_FONT_SECONDARY }}>
              ยังไม่พิมพ์
            </Tag>
          ),
        );
      },
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      width: 168,
      render: (_s: string, record: FactoryParcelItem) => {
        const display = resolveParcelTableStatusDisplay(
          record,
          PARCEL_STATUS_LABEL_TH,
          STATUS_COLOR
        );
        if (display.pendingQueue) {
          const tooltipText = `${PARCEL_PENDING_QUEUE_LINE1} ${PARCEL_PENDING_QUEUE_LINE2}`;
          return wrapParcelCellTooltip(
            tooltipText,
            <Tag
              color={PARCEL_PENDING_QUEUE_TAG_COLOR}
              style={{
                margin: 0,
                fontSize: PARCEL_TABLE_FONT_SECONDARY,
                whiteSpace: "normal",
                lineHeight: "normal",
                padding: "4px 8px",
                textAlign: "center",
              }}
            >
              {PARCEL_PENDING_QUEUE_LINE1}
              <br />
              {PARCEL_PENDING_QUEUE_LINE2}
            </Tag>,
          );
        }
        return wrapParcelCellTooltip(
          display.label,
          <Tag
            color={display.tagColor}
            style={{ margin: 0, fontSize: PARCEL_TABLE_FONT_SECONDARY, whiteSpace: "normal" }}
          >
            {display.label}
          </Tag>,
        );
      },
    },
    {
      title: "การทำงาน",
      key: "actions",
      width: 72,
      fixed: "right" as const,
      render: (_, row) => {
        if (row.speed_queue_status === "FAILED") {
          return wrapParcelCellTooltip(
            "ลองใหม่",
            <Button
              type="default"
              size="small"
              icon={<ReloadOutlined style={{ fontSize: 10 }} />}
              loading={retryingParcelId === row.id}
              onClick={() => void handleRetryQueue(row)}
              aria-label="ลองใหม่"
              style={parcelTableIconActionButtonStyle}
            />,
          );
        }
        if (row.status === "waiting" && row.tracking_number) {
          return (
            <Space size={2} align="start">
              {wrapParcelCellTooltip(
                "พิมพ์ Label",
                <Button
                  type="default"
                  size="small"
                  icon={<PrinterOutlined style={{ fontSize: 10 }} />}
                  onClick={() => handlePrint(row)}
                  aria-label="พิมพ์ Label"
                  style={parcelTableIconActionButtonStyle}
                />,
              )}
              {isParcelCancelable(row) && (
                <Popconfirm
                  title="ยกเลิกพัสดุนี้?"
                  onConfirm={() => handleCancel(row.tracking_number!)}
                >
                  {wrapParcelCellTooltip(
                    "ยกเลิก",
                    <Button
                      type="default"
                      size="small"
                      icon={<CloseOutlined style={{ fontSize: 10 }} />}
                      aria-label="ยกเลิก"
                      style={parcelTableIconActionButtonStyle}
                    />,
                  )}
                </Popconfirm>
              )}
            </Space>
          );
        }
        return wrapParcelCellTooltip(
          "ไม่มีการดำเนินการ",
          <Typography.Text type="secondary" style={{ fontSize: PARCEL_TABLE_FONT }}>
            -
          </Typography.Text>,
        );
      },
    },
  ];



  return (

    <ParcelWrapperAll>

      {/* J.2 Credit Card — เหนือ filter/datatable */}
      <ParcelCreditCard />

      <ParcelHeadWrapper>

        <ParcelSearchForm
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          dateInput={dateInput}
          setDateInput={setDateInput}
          onSearch={() => setPage(1)}
          onClear={() => {
            setViewpoint("all");
            setSelectedBrandCodes([]);
            setPage(1);
          }}
          viewpoint={viewpoint}
          onViewpointChange={(v) => {
            setViewpoint(v);
            setPage(1);
          }}
          viewpointOptions={VIEWPOINT_OPTIONS}
          brandList={brandList}
          selectedBrandCodes={selectedBrandCodes}
          setSelectedBrandCodes={(v) => {
            setSelectedBrandCodes(v);
            setPage(1);
          }}
        />

        <ParcelRightIconWrapper>
          {/* 2026-09-01: ปุ่มเติมเครดิตแบบเรียกได้เสมอ (ไม่ต้องรอเครดิตหมดก่อน) — ไปหน้าเติมเครดิตเอง
           * ผ่าน gateway โดยตรง เดิมมีแค่ทางเดียวคือตอนเครดิตไม่พอ (เปิด InsufficientCreditModal) */}
          <Button style={{ marginRight: 8 }} onClick={() => navigate("/manage-credit/topup")}>
            เติมเครดิต
          </Button>
          {hasEnoughCredit ? (
            <Button
              onClick={() => navigate(MANAGE_PARCEL_CREATE_PATH)}
              style={{ backgroundColor: "#FFD700", borderColor: "#FFD700", color: "#000" }}
            >
              สร้างรายการส่งพัสดุ
            </Button>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Typography.Text type="danger" style={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                เครดิตไม่พอในการสร้างรายการ (ขั้นต่ำ {formatWithCommas(minimumCreditDisplay)} Credits)
              </Typography.Text>
              <Button
                type="default"
                danger
                onClick={() => {
                  setCreditModalPrice(null);
                  setCreditModalOpen(true);
                }}
              >
                รายละเอียด
              </Button>
            </div>
          )}
        </ParcelRightIconWrapper>

      </ParcelHeadWrapper>



      <ParcelCardWrapperTable>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-end",
            marginBottom: 12,
            width: "100%",
          }}
        >
          <Space size={8} style={{ flexShrink: 0 }}>
            {selectedRowKeys.length === 0 ? (
              <Button
                type="primary"
                disabled={getSelectableRowKeys().length === 0}
                onClick={() => setSelectedRowKeys(getSelectableRowKeys())}
              >
                เลือกทั้งหมด
              </Button>
            ) : (
              <>
                <Button type="link" onClick={() => setSelectedRowKeys([])}>
                  ล้างการเลือก
                </Button>
                <Button
                  type="primary"
                  icon={<PrinterOutlined />}
                  onClick={() => void handlePrintAll()}
                  loading={mergeLoading}
                >
                  พิมพ์ทั้งหมด {selectedRowKeys.length} รายการ
                </Button>
              </>
            )}
          </Space>
        </div>

        <Table
          rowKey="id"
          size="small"
          loading={loading}
          rowSelection={rowSelection}
          columns={columns}
          dataSource={items}
          tableLayout="fixed"
          scroll={{ x: 938 }}
          pagination={{

            current: page,

            pageSize: limit,

            total,

            showSizeChanger: true,

            onChange: (p, pageSize) => {

              setPage(p);

              setLimit(pageSize);

            },

          }}

        />

      </ParcelCardWrapperTable>



      {previewModal}

      <Modal
        title={`Label รวม (${mergeSelectionCount || pendingPrintParcelIds.length} รายการ${
          pendingPrintParcelIds.length > 0 && pendingPrintParcelIds.length < mergeSelectionCount
            ? ` — รวมใน PDF ${pendingPrintParcelIds.length} ฉบับ`
            : mergeSelectionCount > 1
              ? " — หลายหน้า"
              : ""
        })`}
        open={mergePreviewOpen}
        onCancel={closeMergePreview}
        footer={null}
        width={560}
        style={{ top: 24 }}
        destroyOnClose
        transitionName=""
        maskTransitionName=""
      >
        <PdfViewerModal
          key={mergedBlobUrl ?? "merge-label"}
          pdfUrl={mergedBlobUrl}
          onAfterPrint={handleMergePreviewAfterPrint}
          showAllPagesOption={mergeSelectionCount > 1}
          defaultViewMode="all"
        />
      </Modal>

      {/* J.3 Insufficient Credit Modal */}
      <InsufficientCreditModal
        open={creditModalOpen}
        onClose={() => setCreditModalOpen(false)}
        estimatePrice={creditModalPrice}
        multiplier={multiplier}
      />

    </ParcelWrapperAll>

  );

};



export default ParcelListTab;

